import Hidden from '@mui/material/Hidden'
import makeStyles from '@mui/styles/makeStyles'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Ad from 'components/Ad'
import Filter from 'components/Filter'
import Layout from 'components/Layout'
import SearchPager from 'components/SearchPager'
import SearchResult from 'components/SearchResult'
import Sort from 'components/Sort'
import SwiperShows from 'components/SwiperShows'
import { getCategories } from 'lib/categories'
import { ROWS_PER_PAGE, SORT_RANKING } from 'lib/constants'
import { getShows } from 'lib/shows'
import { getStations } from 'lib/stations'
import { getCountry } from 'lib/utils'
import dynamic from 'next/dynamic'
import Head from 'next/head'

const DynamicFilterMobile = dynamic(() => import('../components/FilterMobile'), { ssr: false })

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex'
  },
  filter: {
    width: '230px',
    marginRight: theme.spacing(3)
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3)
  },
  sort: {
    marginBottom: theme.spacing(3)
  },
  heading: {
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.body2.fontSize,
      fontWeight: theme.typography.body2.fontWeight
    }
  },
  searchCol: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  billboard: {
    marginBottom: theme.spacing(3),
    '@media (max-width: 500px)': {
      marginBottom: theme.spacing(5)
    }
  },
  '@global': {
    '.ad-search-top, .ad-search-bottom, .ad-search-noresults': {
      width: '100%',
      height: '100px',
      '@media (min-width:768px)': {
        width: '728px',
        height: '90px'
      }
    },
    '.ad-search-top': {
      marginBottom: theme.spacing(3)
    }
  }
}))

export default function Search ({ result, q, total, date, categories, stations, query, newShows, dates }) {
  const classes = useStyles()
  const pageTitle = `Sendung verpasst? ${title(q, date)}`

  const head = (
    <Head>
      <title>{pageTitle}</title>
      <meta name='robots' content='noindex' />
    </Head>
  )

  if (total > 0) {
    return (
      <>
        {head}
        <Layout search={q}>
          <div className={classes.container}>
            <Hidden mdDown>
              <div className={classes.filter}>
                <Filter param='date' title='Datum' items={dates} query={query} />
                <Filter param='station' title='Sender' items={stations} query={query} />
                <Filter param='cat' title='Rubriken' items={categories} query={query} />
              </div>
            </Hidden>
            <div className={classes.results}>
              <Hidden mdUp>
                <DynamicFilterMobile categories={categories} stations={stations} dates={dates} query={query} />
              </Hidden>
              <div className={classes.resultsHeader}>
                <Typography
                  variant='h2'
                  component='h1'
                  gutterBottom
                  className={classes.heading}
                  dangerouslySetInnerHTML={{ __html: resultStats(total, q, date) }}
                />
                <Hidden mdDown>
                  <div className={classes.searchCol}>
                    <Sort query={query} className={classes.sort} />
                  </div>
                </Hidden>
              </div>
              <Ad slot={process.env.NEXT_PUBLIC_ADSENSE_SLOT_SEARCH_TOP} classname='ad-search-top' key={Math.random()} />
              {result.map((result, index) => (
                <SearchResult key={index} result={result} />
              ))}
              <Ad slot={process.env.NEXT_PUBLIC_ADSENSE_SLOT_SEARCH_BOTTOM} classname='ad-search-bottom' key={Math.random()} />
              <SearchPager total={total} query={query} />
            </div>
          </div>
        </Layout>
      </>
    )
  } else {
    return (
      <>
        {head}
        <Layout search={q}>
          <Typography variant='h1' component='h1' gutterBottom className={classes.heading}>Suche</Typography>
          {q
            ? <Alert severity='warning'>Ihre Suche nach <b>{q}</b> ergab leider keine Ergebnisse.</Alert>
            : <Alert severity='warning'>Ihre Suche ergab leider keine Ergebnisse.</Alert>}
          <p>
            Die Sender stellen immer mehr Beiträge ins Netz. Leider ist nicht alles online verfügbar oder das Video
            wurde wieder entfernt. Möglicherweise bringt ein anderer Suchbegriff das gewünschte Ergebnis.
          </p>
          <Ad slot={process.env.NEXT_PUBLIC_ADSENSE_SLOT_SEARCH_NORESULTS} classname='ad-search-noresults' fullWidth key={Math.random()} />
          <SwiperShows title='Neu eingestellt' items={newShows} />
        </Layout>
      </>
    )
  }
}

function resultStats (total, q, date) {
  let text = total.toLocaleString('de-DE') + ' Ergebnis' + (total > 1 ? 'se' : '')
  if (q) {
    text += ' für <strong>' + q + '</strong>'
  }
  if (date) {
    text += ' vom ' + date
  }
  return text
}

function title (q, date) {
  let text = ''
  if (q && date) {
    text += q + ' vom ' + date
  } else if (date) {
    text += ' Sendungen vom ' + date
  } else if (q) {
    text += q
  } else {
    text = 'Suche'
  }
  return text
}

export async function getServerSideProps ({ res, query }) {
  // filter criteria
  const q = query.q === undefined ? null : query.q
  const date = query.date === undefined ? null : query.date
  const category = query.cat
  const station = query.station
  const sort = query.sort
  const page = query.page
  const country = getCountry()

  const body = await getShows({ term: q, date, size: ROWS_PER_PAGE, aggregate: true, category, station, sort, page, country })

  const result = body.hits.hits
  const total = body.hits.total.value
  // aggregations
  const categories = body.aggregations.categories.categories.buckets
  addName(categories, getCategories())
  const stations = body.aggregations.stations.stations.buckets
  addName(stations, getStations())
  const dateBuckets = body.aggregations.dates.buckets
  const dates = buildDateFilters(dateBuckets)

  // no results
  let newShows = null
  if (total === 0) {
    const newResult = await getShows({ sort: SORT_RANKING, country })
    newShows = newResult.hits.hits
  }

  return {
    props: {
      result,
      q,
      date,
      total,
      categories,
      stations,
      query,
      newShows,
      dates
    }
  }
}

export function isActive (query, key, value) {
  if (!Object.prototype.hasOwnProperty.call(query, key) && value === 'all') {
    return true
  } else {
    return query[key] === value
  }
}

function addName (results, values) {
  for (const result of results) {
    const value = values.find(item => item.slug === result.key)
    if (value) {
      result.name = value.name
    }
  }
}

function buildDateFilters (results) {
  const filter = []
  for (const result of results) {
    if (result.doc_count > 0) {
      filter.push({ key: result.from_as_string, name: result.key.substring(3), order: result.key.substring(0, 2), doc_count: result.doc_count })
    }
  }
  return filter.sort((a, b) => a.order - b.order)
}
